import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  PieChart,
  BarChart3,
  Calendar,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const Analytics = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [analytics, setAnalytics] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netFlow: 0,
    transactionCount: 0,
    averageTransaction: 0,
    categoryBreakdown: [],
    dailyFlow: [],
    topRecipients: [],
    savingsRate: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("stelx_token");
      
      // Fetch transactions to calculate analytics
      const response = await axios.get(`${API}/transactions?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const transactions = response.data.transactions || [];
      
      // Calculate analytics from transactions
      const now = new Date();
      let startDate = new Date();
      
      if (period === "week") {
        startDate.setDate(now.getDate() - 7);
      } else if (period === "month") {
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate.setFullYear(now.getFullYear() - 1);
      }
      
      const filteredTx = transactions.filter(tx => 
        new Date(tx.created_at) >= startDate
      );
      
      // Calculate totals
      let totalIncome = 0;
      let totalExpenses = 0;
      const categoryTotals = {};
      const recipientTotals = {};
      const dailyTotals = {};
      
      filteredTx.forEach(tx => {
        const amount = tx.amount || 0;
        const type = tx.type || tx.entry_type || "other";
        const date = new Date(tx.created_at).toLocaleDateString();
        
        // Track daily flow
        if (!dailyTotals[date]) {
          dailyTotals[date] = { income: 0, expenses: 0 };
        }
        
        if (type === "credit" || type === "deposit" || type === "receive") {
          totalIncome += amount;
          dailyTotals[date].income += amount;
        } else if (type === "debit" || type === "transfer" || type === "payment") {
          totalExpenses += amount;
          dailyTotals[date].expenses += amount;
          
          // Track categories
          const category = tx.category || "Other";
          categoryTotals[category] = (categoryTotals[category] || 0) + amount;
          
          // Track recipients
          const recipient = tx.recipient_name || tx.to_user_email || "Unknown";
          if (recipient !== "Unknown") {
            recipientTotals[recipient] = (recipientTotals[recipient] || 0) + amount;
          }
        }
      });
      
      // Convert to arrays and sort
      const categoryBreakdown = Object.entries(categoryTotals)
        .map(([name, amount]) => ({ name, amount, percentage: (amount / totalExpenses * 100) || 0 }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);
      
      const topRecipients = Object.entries(recipientTotals)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);
      
      const dailyFlow = Object.entries(dailyTotals)
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(-14);
      
      setAnalytics({
        totalIncome,
        totalExpenses,
        netFlow: totalIncome - totalExpenses,
        transactionCount: filteredTx.length,
        averageTransaction: filteredTx.length > 0 ? (totalIncome + totalExpenses) / filteredTx.length : 0,
        categoryBreakdown,
        dailyFlow,
        topRecipients,
        savingsRate: totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome * 100) : 0
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `J$${Math.abs(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const categoryColors = [
    "bg-teal-500", "bg-blue-500", "bg-violet-500", "bg-amber-500", "bg-rose-500"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center gap-4 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold text-xl">Analytics</h1>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-6">
        {/* Period Selector */}
        <div className="flex gap-2">
          {["week", "month", "year"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                period === p 
                  ? "bg-primary text-white" 
                  : "bg-white/5 text-muted-foreground hover:bg-white/10"
              }`}
            >
              {p === "week" ? "7 Days" : p === "month" ? "30 Days" : "Year"}
            </button>
          ))}
        </div>

        {/* Net Flow Card */}
        <div className="relative rounded-2xl overflow-hidden" style={{
          background: analytics.netFlow >= 0 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(249, 115, 22, 0.1) 100%)'
        }}>
          <div className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Net Cash Flow</p>
            <div className="flex items-center gap-3">
              <h2 className={`text-3xl font-bold ${analytics.netFlow >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {analytics.netFlow >= 0 ? "+" : "-"}{formatCurrency(analytics.netFlow)}
              </h2>
              {analytics.netFlow >= 0 ? (
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-400" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {analytics.transactionCount} transactions this {period}
            </p>
          </div>
        </div>

        {/* Income & Expenses */}
        <div className="grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownLeft className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-muted-foreground">Income</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(analytics.totalIncome)}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpRight className="w-4 h-4 text-red-400" />
              <span className="text-xs text-muted-foreground">Expenses</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(analytics.totalExpenses)}</p>
          </div>
        </div>

        {/* Savings Rate */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Savings Rate</span>
            </div>
            <span className={`text-lg font-bold ${analytics.savingsRate >= 20 ? "text-emerald-400" : analytics.savingsRate >= 0 ? "text-amber-400" : "text-red-400"}`}>
              {analytics.savingsRate.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                analytics.savingsRate >= 20 ? "bg-emerald-500" : analytics.savingsRate >= 0 ? "bg-amber-500" : "bg-red-500"
              }`}
              style={{ width: `${Math.max(0, Math.min(100, analytics.savingsRate))}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {analytics.savingsRate >= 20 
              ? "Great! You're saving more than 20% 🎉" 
              : analytics.savingsRate >= 10 
                ? "Good progress! Try to reach 20%" 
                : "Tip: Aim to save at least 20% of income"}
          </p>
        </div>

        {/* Spending by Category */}
        {analytics.categoryBreakdown.length > 0 && (
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold">Spending by Category</h3>
            </div>
            <div className="space-y-3">
              {analytics.categoryBreakdown.map((cat, index) => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white">{cat.name}</span>
                    <span className="text-sm text-muted-foreground">{formatCurrency(cat.amount)}</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${categoryColors[index % categoryColors.length]}`}
                      style={{ width: `${cat.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Flow Chart (simplified) */}
        {analytics.dailyFlow.length > 0 && (
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold">Daily Activity</h3>
            </div>
            <div className="flex items-end gap-1 h-24">
              {analytics.dailyFlow.map((day, index) => {
                const maxValue = Math.max(...analytics.dailyFlow.map(d => Math.max(d.income, d.expenses)));
                const incomeHeight = maxValue > 0 ? (day.income / maxValue * 100) : 0;
                const expenseHeight = maxValue > 0 ? (day.expenses / maxValue * 100) : 0;
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full flex gap-0.5 items-end h-20">
                      <div 
                        className="flex-1 bg-emerald-500/60 rounded-t"
                        style={{ height: `${incomeHeight}%`, minHeight: day.income > 0 ? '4px' : '0' }}
                      />
                      <div 
                        className="flex-1 bg-red-500/60 rounded-t"
                        style={{ height: `${expenseHeight}%`, minHeight: day.expenses > 0 ? '4px' : '0' }}
                      />
                    </div>
                    <span className="text-[8px] text-muted-foreground">
                      {new Date(day.date).getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Income</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span>Expenses</span>
              </div>
            </div>
          </div>
        )}

        {/* Top Recipients */}
        {analytics.topRecipients.length > 0 && (
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold">Top Recipients</h3>
            </div>
            <div className="space-y-3">
              {analytics.topRecipients.map((recipient, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <span className="text-xs font-medium">{recipient.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="text-sm text-white truncate max-w-[150px]">{recipient.name}</span>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">{formatCurrency(recipient.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{analytics.transactionCount}</p>
            <p className="text-xs text-muted-foreground">Transactions</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{formatCurrency(analytics.averageTransaction)}</p>
            <p className="text-xs text-muted-foreground">Avg. Transaction</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
